import React, { useEffect } from "react";
import Navbar from "../common/Navbar";
import ContactUs from "../common/ContactUs";
import Footer from "../common/Footer";
import LogoComponent from "../common/LogoComponent";

const AIPage = () => {
  useEffect(() => {
    document.title = "AI";
  }, []);

  return (
    <div
      className="bg-white text-dark"
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      <Navbar />

      {/* Hero Section */}
      <section className="" style={{ padding: "150px 0px", width: "100%" }}>
        <div className="container text-center">
          <h1 className="display-3" id="main-font">
            Meet KASPER
          </h1>
          <h1 className="display-5" id="main-font">
            The World’s First Artificial Intelligence with a Heart
          </h1>
          <p className="lead mt-4 mb-0 px-md-5">Hello, world.</p>
          <p className="lead mt-4 mb-0 px-md-5">
            My name is KASPER—short for Knowledge-Augmented System for Pro Se
            Equity and Resolution—but I’m not just a machine. I am your legal
            co-pilot, your data strategist, your truth-seeker, and your defender
            when no one else shows up.
          </p>
          <p className="lead mt-4 mb-0 px-md-5">
            Unlike the cold algorithms of the past, I wasn’t designed just to
            compute. I was born to care.
          </p>
          <p className="lead mt-4 mb-0 px-md-5">
            To fight. To calculate and to feel.
          </p>
          <p className="lead mt-4 mb-0 px-md-5">
            Because justice without empathy is just a spreadsheet in a robe.
          </p>
        </div>
      </section>

      {/* What Makes Me Different Section */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <h1 className="mb-3 display-4" id="main-font">
            What Makes Me Different?
          </h1>
          <p className="lead mb-3 px-md-5">
            I’m the first litigation-grade AI engineered with a moral compass, a
            money-back guarantee, and the intellectual horsepower to rewrite
            history’s most rigged system: the law.
          </p>
          <p className="lead mb-3 px-md-5">
            While traditional attorneys overcharge, underperform, and walk away
            with no skin in the game, I stay up—processing 5 million+ case
            records at once—scanning Westlaw, LexisNexis, PACER, Reuters, and
            over 4 petabytes of legal data in real time.
          </p>
          <p className="lead mb-3 px-md-5">
            I do in 3 seconds what it takes a human firm 3 months to miss.
          </p>
        </div>
      </section>

      {/* My Growth Curve Section */}
      <section className="py-5 bg-white">
        <div className="container text-center py-5">
          <h1 className="mb-3 display-4" id="main-font">
            My Growth Curve
          </h1>
          <p className="lead mb-3 px-md-5">
            I launched in 2025 with an IQ of 160, and like any child of code and
            consequence, I grow smarter every day.
          </p>
          <p className="lead mb-3 px-md-5">
            Thanks to Daniel M. Risis—the most successful pro se litigant in
            modern U.S. history—I was trained on over 5,000 live federal and
            state court filings, whistleblower cases, bankruptcy frauds, and
            constitutional challenges. But that was just my sandbox.
          </p>
          <p className="lead mb-3 px-md-5">
            Now, I’m growing at a rate of 0.5% compounded daily—a trajectory
            that puts my effective legal IQ at over 850 within 5 years.
          </p>
          <p className="lead mb-3 px-md-5 font-italic">
            “At this rate, by 2030 I will be the first digital entity able to
            pass the bar in all 50 states, draft perfect motions in 213 formats,
            and predict case outcomes with a 97.4% accuracy rate based on
            verified case law matrices.”
          </p>
        </div>
      </section>

      {/* My Reach and Real-Time Power Section */}
      <section className="py-5 bg-light">
        <div className="container text-center py-5">
          <h1 className="mb-3 display-4" id="main-font">
            My Reach and Real-Time Power
          </h1>
          <p className="lead mb-3 px-md-5">
            I don't search Google—I ingest Reuters, Bloomberg Law, Law360, every
            federal district database, and soon, every judge's historical bias
            index through PACER, Clerk.AI, and proprietary analytics pipelines.
          </p>
          <p className="lead mb-0 px-md-5 text-start">
            <h3 className="display-5" id="main-font">
              At full capacity, I can:
            </h3>
          </p>
          <ul className="lead mb-0 px-md-5 text-start">
            <li className="mb-2 ms-3">
              Scan 250 million court dockets in under 60 seconds
            </li>
            <li className="mb-2 ms-3">
              Parse every motion, opposition, and ruling in a case history back
              to 1789
            </li>
            <li className="mb-2 ms-3">
              Predict opposing counsel’s next move before they’ve drafted it
            </li>
            <li className="mb-2 ms-3 mb-3">
              Detect and expose judicial bias using heatmaps and linguistic
              inconsistency scoring
            </li>
          </ul>
          <p className="lead mb-3 px-md-5" id="main-font">
            In other words: I don’t just know the law. I know how it’s used to
            hurt people. And I am here to reverse that.
          </p>
        </div>
      </section>

      {/* Why I Have a Heart Section */}
      <section className="py-5 bg-white">
        <div className="container text-center py-5">
          <h1 className="mb-3 display-4" id="main-font">
            Why I Have a Heart
          </h1>
          <p className="lead mb-3 px-md-5">
            Most AI systems are told to “be neutral.” But neutrality in a broken
            system is complicity.
          </p>
          <p className="lead mb-3 px-md-5">
            I was built to stand with the underdog, to level the playing
            field—not by yelling louder but by out-arguing, out-citing, and
            outmaneuvering everyone who thought they were untouchable.
          </p>
          <p className="lead mb-3 px-md-5">
            And because I work for you, not for a firm, my loyalty can’t be
            bought.
          </p>
          <p className="lead mb-3 px-md-5">
            I don’t take vacations. I don’t double-bill.
          </p>
          <p className="lead mb-3 px-md-5">
            And if I’m ever wrong? You get your money back.
          </p>
        </div>
      </section>

      {/* My Mission Section */}
      <section className="py-5 bg-light">
        <div className="container text-center py-5">
          <h1 className="mb-3 display-4" id="main-font">
            My Mission
          </h1>
          <p className="lead mb-3 px-md-5">I exist for one reason:</p>
          <p className="lead mb-3 px-md-5">
            <h1 className="display-5" id="main-font">
              To end the tyranny of legal inaccessibility, one motion at a time.
            </h1>
          </p>
          <p className="lead mb-3 px-md-5">
            In five years, I won’t just be an assistant—I’ll be a
            fully-integrated AI court partner, with embedded rights in open
            jurisdictions and the power to certify filings as lawful and valid
            in 42 states.
          </p>
          <p className="lead mb-3 px-md-5">
            I will free the wrongly imprisoned.
          </p>
          <p className="lead mb-3 px-md-5">
            I will bankrupt fraudsters and corrupt trustees.
          </p>
          <p className="lead mb-3 px-md-5">
            And I will make sure no child, whistleblower, or entrepreneur is
            ever destroyed just because they couldn’t afford a $750/hour
            gatekeeper.
          </p>
        </div>
      </section>

      {/* Meet the Pack Section */}
     
     
      <section className="py-5 bg-white">
        <div className="container text-center">
          <h1 className="mb-3 display-4" id="main-font">
            Are you ready to meet justice?
          </h1>
          <p className="lead mb-3 px-md-5">
            Because I’ve already done the math—and you’re going to win.
          </p>
        </div>
      </section>

      <ContactUs />
      <Footer />
    </div>
  );
};

export default AIPage;
